import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  createParentBridge,
  BridgeParentEventType,
  BridgeReadyStatus,
  PayloadError,
  PayloadParentToChild,
  ParentBridgeOptions,
  BRIDGE_READY_TIMEOUT_MS,
} from "iframe-bridge"

import { BridgeContext } from "./BridgeContext"
import { BridgeDiagnosticReason, IBridgeDiagnosticLabels } from "./types"

const REASON_TO_LABEL_KEY: Record<
  Exclude<BridgeDiagnosticReason, null>,
  keyof IBridgeDiagnosticLabels
> = {
  "sync-not-seen": "syncNotSeen",
  "ack-not-received": "ackNotReceived",
  "origin-rejected": "originRejected",
}

type DiagnosticsState = {
  reason: BridgeDiagnosticReason
  syncSeen: boolean
  rejectedOrigin: string | null
  lastMessageAt: number | null
  startedAt: number | null
}

const INITIAL_DIAGNOSTICS: DiagnosticsState = {
  reason: null,
  syncSeen: false,
  rejectedOrigin: null,
  lastMessageAt: null,
  startedAt: null,
}

export interface IBridgeProviderProps
  extends Pick<ParentBridgeOptions, "onEvent" | "metaGetter" | "onDisconnect"> {
  enabled?: boolean
  iframeOrigin: string // e.g. "https://iframe.example.com"
  iframeUrl: string // e.g. "https://iframe.example.com/embed"
  readyTimeoutMs?: number
  // Generic reason -> label for a consumer's own debug UI. The bridge never
  // interprets these strings, it only resolves diagnostics.code from them.
  diagnosticLabels?: IBridgeDiagnosticLabels
  children: ReactNode
}

export const BridgeProvider = ({
  iframeOrigin,
  iframeUrl,
  children,
  enabled = false,
  readyTimeoutMs = BRIDGE_READY_TIMEOUT_MS,
  diagnosticLabels,
  onEvent,
  onDisconnect,
  metaGetter,
}: IBridgeProviderProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const bridgeRef = useRef<ReturnType<typeof createParentBridge> | null>(null)
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [ready, setReady] = useState(false)
  const [status, setStatus] = useState<BridgeReadyStatus>("idle")
  const [lastError, setLastError] = useState<PayloadError | null>(null)
  const [diagnosticsState, setDiagnosticsState] =
    useState<DiagnosticsState>(INITIAL_DIAGNOSTICS)

  const clearReadyTimeout = useCallback(() => {
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current)
      readyTimeoutRef.current = null
    }
  }, [])

  // Arms a fresh readyTimeoutMs window. Must be called on every open, since
  // `ready` is otherwise sticky: the child only reports bridge:disconnect when
  // it explicitly calls disconnect() (no unload/pagehide hook), so a stale
  // "ready" from a previous session would otherwise survive into this one.
  const beginConnection = useCallback(() => {
    clearReadyTimeout()
    setLastError(null)
    setReady(false)
    setStatus("connecting")
    setDiagnosticsState({ ...INITIAL_DIAGNOSTICS, startedAt: Date.now() })
    readyTimeoutRef.current = setTimeout(() => {
      setStatus("failed")
      // syncSeen alone fully explains why the handshake never finished —
      // either the child never started it, or it started and never
      // completed. See BridgeDiagnosticReason for why there's no 3rd case.
      setDiagnosticsState((d) => ({
        ...d,
        reason: d.syncSeen ? "ack-not-received" : "sync-not-seen",
      }))
    }, readyTimeoutMs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyTimeoutMs])

  const endConnection = useCallback(() => {
    clearReadyTimeout()
    setStatus("idle")
  }, [clearReadyTimeout])

  useEffect(() => {
    if (!enabled) return

    const bridge = createParentBridge({
      allowedChildOrigins: [iframeOrigin],
      childTargetOrigin: iframeOrigin,
      metaGetter,
      childWindowGetter: () => iframeRef.current?.contentWindow ?? null,
      onReady: () => {
        clearReadyTimeout()
        setReady(true)
        setStatus("ready")
        setDiagnosticsState((d) => ({
          ...d,
          reason: null,
          lastMessageAt: Date.now(),
        }))
      },
      onError: (error) => setLastError(error),
      onEvent,
      onDisconnect: () => {
        clearReadyTimeout()
        setReady(false)
        setStatus("idle")
        onDisconnect?.()
      },
      onSync: () => {
        setDiagnosticsState((d) => ({
          ...d,
          syncSeen: true,
          lastMessageAt: Date.now(),
        }))
      },
      onRejectedOrigin: (origin) => {
        setDiagnosticsState((d) => ({
          ...d,
          reason: "origin-rejected",
          rejectedOrigin: origin,
          lastMessageAt: Date.now(),
        }))
      },
    })

    bridgeRef.current = bridge
    bridge.attach()

    return () => {
      bridge.detach()
      bridgeRef.current = null
      clearReadyTimeout()
      setReady(false)
      setStatus("idle")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeOrigin, enabled])

  const diagnosticCode = useMemo(() => {
    if (!diagnosticsState.reason) return undefined
    return diagnosticLabels?.[REASON_TO_LABEL_KEY[diagnosticsState.reason]]
  }, [diagnosticsState.reason, diagnosticLabels])

  const api = useMemo(() => {
    return {
      iframeRef,
      iframeUrl,
      iframeOrigin,
      enabled,
      ready,
      status,
      lastError,
      diagnostics: { ...diagnosticsState, code: diagnosticCode },
      beginConnection,
      endConnection,
      // Fire-and-forget cleanup: must never throw, so it can't block a close
      // path (e.g. when called from inside a setParams updater).
      sendCancelProcess: () => {
        const bridge = bridgeRef.current
        if (!bridge) return false
        try {
          bridge.sendCancelProcess()
          return true
        } catch {
          return false
        }
      },
      // Parent -> Child: send message to child
      send(type: BridgeParentEventType, payload: PayloadParentToChild) {
        if (!enabled) return
        try {
          const bridge = bridgeRef.current
          if (!bridge) throw new Error("bridge not initialized")
          bridge.send(type, payload)
        } catch (e) {
          setLastError({ type, message: String(e?.message || e) })
          throw e
        }
      },
    }
  }, [
    iframeUrl,
    iframeOrigin,
    ready,
    status,
    enabled,
    lastError,
    diagnosticsState,
    diagnosticCode,
    beginConnection,
    endConnection,
  ])

  return <BridgeContext.Provider value={api}>{children}</BridgeContext.Provider>
}
